import { useCallback, useState, useEffect } from 'react'
import { navigate } from 'gatsby'
import useLocation from 'react-use/lib/useLocation'
import useSearchParam from 'react-use/lib/useSearchParam'

import useUrlPathBuilder from './useUrlPathBuilder'

interface UseSearchFormParams {
  defaultValue?: string
  prefix?: string | null
}

interface UseSearchForm {
  searchQueryParam: string
  searchQuery: string
  searching: boolean
  htmlSearchAction: string
  handleUpdateSearchQuery: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  >
  handleSearchSubmit: React.FormEventHandler<HTMLElement>
  updateSearchQuery: React.Dispatch<React.SetStateAction<string>>
}

export default function useSearchForm(
  params: UseSearchFormParams = {}
): UseSearchForm {
  const searchQueryParam = useSearchParam('s') || ''
  const [searchQuery, updateSearchQuery] = useState(
    params.defaultValue ?? searchQueryParam
  )
  const [searching, setSearching] = useState(false)
  const { urlPathBuilder, blogPrefix } = useUrlPathBuilder()
  const { pathname } = useLocation()
  const htmlSearchAction = urlPathBuilder([
    blogPrefix,
    params.prefix ? params.prefix : 'search',
  ])

  const handleSearchSubmit = useCallback<React.FormEventHandler<HTMLElement>>(
    (event) => {
      event.preventDefault()
      setSearching(
        !(pathname?.includes('search') || pathname?.includes('resume-example'))
      )
      navigate(
        urlPathBuilder([htmlSearchAction], {
          search: {
            s: searchQuery,
          },
        })
      )

      updateSearchQuery('')
    },
    [urlPathBuilder, htmlSearchAction, searchQuery, setSearching, pathname]
  )

  const handleUpdateSearchQuery = useCallback<
    React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
  >((event) => updateSearchQuery(event.target.value), [])

  // If the search query param changes, update the input to use that instead of
  // it's current value as the user submitted the form from maybe not this
  // component.
  useEffect(() => {
    if (params.defaultValue === undefined) {
      updateSearchQuery(searchQueryParam)
    }
  }, [searchQueryParam, params.defaultValue])

  return {
    searchQueryParam,
    searchQuery,
    searching,
    handleUpdateSearchQuery,
    handleSearchSubmit,
    htmlSearchAction,
    updateSearchQuery,
  }
}
